import {useMemo, useRef, useState, type CSSProperties} from "react";
import {numberOfBackground} from "libs/bgNumbering";
import {useBackgroundNumbers} from "hooks/useBackgroundNumbers";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import cn from "classnames";

import {API} from "api";
import {useSetting} from "contexts/setting";
import {ALL_PRODUCT, RELEASES_SETS} from "data/constants";
import {palettes} from "data/palettes";
import {PRODUCTS, productCodesForChannel} from "data/products";
import {offersGenerativeTile, releasesSetsForProduct} from "data/products/index";
import {useBgCatalogIndex} from "hooks/useBgCatalogIndex";
import {useCurrentSet} from "hooks/useCurrentSet";
import {
  getChannelSettingsQueryKey,
  useChannelSettings,
  type ChannelSettings,
} from "hooks/useChannelSettings";
import {isDraftUploadVariation} from "libs/bgIdentity";
import {
  hiddenBackgroundsInSet,
  hiddenBackgroundsOf,
  hiddenProductsOf,
  hiddenSetsOf,
  withHiddenChange,
  type BackgroundId,
  type HiddenChange,
} from "libs/selectorVisibility";
import {galleryTiles} from "../BgSelector/galleryTiles";
import {generativePreview} from "../BgSelector/generativePreview";
import CheckboxGroup from "./CheckboxGroup";
import LayoutVisibilityGroup, {
  layoutVisibilityRows,
  type OptionCheckbox,
} from "./LayoutVisibilityGroup";
import {
  ProductStageVisibilityGroup,
  SetStageVisibilityGroup,
  channelStages,
} from "./StageVisibilityGroups";

/**
 * Admin control for what the selectors offer. An admin takes out a product
 * that is not needed now, broken or not ready, a releases set, or a
 * background tile: clear its checkbox and it leaves the Product dropdown, the
 * Set dropdown or the background tiles of every user. Set the checkbox again
 * and it comes back.
 *
 * Only the offer changes (libs/selectorVisibility.ts). A link that names a
 * hidden option opens it, and saved scenes keep it. To delete a background,
 * use the background list editor (the pencil in the Background section).
 *
 * Each click saves at once (PATCH /api/v3/channel-settings/hidden), one
 * option per request, and the lists show the change before the request
 * completes. Products are hidden per channel or per stage, sets on the
 * releases channel (wherever a releases product is listed) per channel or per
 * stage (StageVisibilityGroups.tsx), backgrounds per gallery: the
 * background list of the page's (channel, type, product). On releases the
 * backgrounds are a table with a column per set: "All" hides a background in
 * every set, a set column in that set only, so each set offers its own group
 * of the product's backgrounds. The built-in generative gradient is a row of
 * the backgrounds too. Layouts are hidden per stage and set
 * (LayoutVisibilityGroup.tsx).
 *
 * The server lets an admin change the backgrounds only
 * (editBackgroundVisibility); products, sets and layouts need
 * editSelectorVisibility (superadmin). `showsOptions` and `showsBackgrounds`
 * give the parts that the role sees.
 */

/** The summary of a list: how many of its options the selector hides. */
function hiddenSummary(items: Array<{isHidden: boolean}>) {
  const hidden = items.filter((item) => item.isHidden).length;
  return {
    summary: hidden > 0 ? `${hidden} of ${items.length} hidden` : `all ${items.length} shown`,
    isChanged: hidden > 0,
  };
}

/** Sets are a releases concept, so their hidden list is on that channel. */
const SETS_CHANNEL = "releases";

interface Option {
  label: string;
  value: string;
}

/** One background of the visibility list: a gallery tile or a built-in tile. */
interface BgRow {
  id: BackgroundId;
  /** The short name under the thumbnail. */
  name: string;
  /** The tooltip. */
  title: string;
  /** For screen readers: "Background 21", "Generative gradient". */
  label: string;
  /** The label inside a sentence: "background 21", "generative gradient". */
  phrase: string;
  thumbStyle: CSSProperties;
}

export default function SelectorVisibilitySection({
  bgDisplayOrder,
  allBgUrls,
  showsOptions,
  showsBackgrounds,
}: {
  /** The gallery's variations in tile order, or null off the catalog path. */
  bgDisplayOrder: number[] | null;
  /** The legacy url arrays of the gallery. */
  allBgUrls: unknown[] | null | undefined;
  /** Show the products, sets and layouts (feature defaults.selector-visibility). */
  showsOptions: boolean;
  /** Show the background tiles (feature defaults.background-visibility). */
  showsBackgrounds: boolean;
}) {
  // `channel` is the entry channel: its Product dropdown is the one an admin
  // edits. The gallery is the page's data scope (data/offering.ts). The
  // editor's provider always holds a selection; the context default does not.
  const {channel, type, dataChannel, dataType, product, bgOption} = useSetting() as unknown as {
    channel: Option;
    type: Option;
    dataChannel: string;
    dataType: string;
    product: Option;
    bgOption: string | number | null | undefined;
  };
  const queryClient = useQueryClient();
  const {data: productSettings} = useChannelSettings(channel.value);
  const {data: setSettings} = useChannelSettings(SETS_CHANNEL);
  const {data: gallerySettings} = useChannelSettings(dataChannel);
  const catalogIndex = useBgCatalogIndex(dataChannel, dataType);
  const backgroundNumbers = useBackgroundNumbers();
  const currentSet: string | undefined = useCurrentSet();
  const [hasFailed, setHasFailed] = useState(false);

  const products: Option[] = useMemo(() => {
    const codes = new Set<string>(productCodesForChannel(channel.value));
    const list = (PRODUCTS as Option[])
      .filter((item) => codes.has(item.value))
      .map(({label, value}) => ({label, value}));
    // "All Products" is an entry of the releases dropdown (What's New) too.
    return channel.value === "releases" ? [ALL_PRODUCT as Option, ...list] : list;
  }, [channel.value]);

  const isAllProducts = product.value === ALL_PRODUCT.value;
  // The same tiles BgSelector draws. ALL products and livestream draw no
  // numbered gallery; a draft upload has no lasting identity to hide.
  const tiles = useMemo(() => {
    if (isAllProducts || channel.value === "livestream") return [];
    return galleryTiles({
      catalogIndex,
      displayOrder: bgDisplayOrder,
      allBgUrls,
      product: product.value,
      isAllProducts,
      isGoogleDisplayNetwork: channel.value === "ads" && type.value === "google-display-network",
    }).filter((tile) => !isDraftUploadVariation(tile.variation));
  }, [
    isAllProducts,
    channel.value,
    type.value,
    catalogIndex,
    bgDisplayOrder,
    allBgUrls,
    product.value,
  ]);

  // A server from before the built-in rules sends no
  // `hiddenBuiltInBackgrounds`. It would refuse a mark on the gradient.
  const knowsBuiltInRules = gallerySettings?.hiddenBuiltInBackgrounds !== undefined;
  const showsGenerative =
    knowsBuiltInRules &&
    channel.value !== "livestream" &&
    offersGenerativeTile(channel.value, product.value);
  // The rows in the order of the selector: the gallery tiles, then the
  // generative gradient. A row is named by its place in the table, which a
  // hidden mark does not change; the tooltip gives the tile number of the
  // page (libs/bgNumbering.ts), which skips the hidden backgrounds.
  const bgRows: BgRow[] = useMemo(() => {
    const rows: BgRow[] = tiles.map((tile, index) => {
      const pageNumber = backgroundNumbers
        ? numberOfBackground(String(tile.variation), backgroundNumbers)
        : null;
      return {
        id: tile.variation,
        name: String(index + 1),
        title:
          pageNumber !== null
            ? `Background ${index + 1}: tile ${pageNumber} of this page (?bg=${pageNumber})`
            : `Background ${index + 1}: no tile on this page`,
        label: `Background ${index + 1}`,
        phrase: `background ${index + 1}`,
        thumbStyle: {"--thumb": `url("${tile.thumbnailUrl}")`} as CSSProperties,
      };
    });
    if (showsGenerative) {
      const {backgroundImage, baseColor} = generativePreview(
        (palettes as Record<string, string[]>)[product.label]
      );
      rows.push({
        id: "generative",
        name: "Gen",
        title: "Generative gradient (?background=generative)",
        label: "Generative gradient",
        phrase: "the generative gradient",
        // Over the checkerboard of the image tiles: one image per spot.
        thumbStyle: {
          backgroundImage,
          backgroundColor: baseColor,
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
        },
      });
    }
    return rows;
  }, [tiles, backgroundNumbers, showsGenerative, product.label]);

  // The sets of the gallery: on releases, each one can show its own group of
  // the backgrounds.
  const gallerySets: Option[] = useMemo(
    () =>
      dataChannel === SETS_CHANNEL && !isAllProducts
        ? (releasesSetsForProduct(product.value) as Option[])
        : [],
    [dataChannel, isAllProducts, product.value]
  );

  const hiddenProducts = hiddenProductsOf(productSettings);
  const hiddenSets = hiddenSetsOf(setSettings, dataType, product.value);
  const hiddenBgs = hiddenBackgroundsOf(gallerySettings, dataType, product.value);

  // A click shows at once: the checkbox reads its own click until the server
  // confirms it, and the selectors read the change from the query cache.
  // Clicks can overlap, so the lists are read back from the server only when
  // the last request has completed: an early answer cannot undo a later
  // click on the screen.
  const [unconfirmed, setUnconfirmed] = useState<Record<string, boolean>>({});
  const pending = useRef(0);
  const {mutate: saveChange} = useMutation({
    mutationFn: (change: HiddenChange) => API.patchHiddenSelectorOption(change),
    onMutate: async (change) => {
      pending.current += 1;
      setHasFailed(false);
      const queryKey = getChannelSettingsQueryKey(change.channel);
      await queryClient.cancelQueries({queryKey});
      queryClient.setQueryData<ChannelSettings>(queryKey, (current) =>
        withHiddenChange(current ?? {channel: change.channel, stageOrder: null}, change)
      );
    },
    onError: (err) => {
      console.error("[SelectorVisibilitySection] Save failed:", err);
      setHasFailed(true);
    },
    onSettled: async () => {
      pending.current -= 1;
      if (pending.current > 0) return;
      await queryClient.invalidateQueries({queryKey: ["channel-settings"]});
      if (pending.current === 0) setUnconfirmed({});
    },
  });

  const changeKey = (option: Omit<HiddenChange, "hidden">) =>
    [option.kind, option.channel, option.type, option.product, option.set, option.value].join(":");

  /** Save one change; the checkbox shows it until the server confirms it. */
  const applyChange = (change: HiddenChange) => {
    setUnconfirmed((current) => ({...current, [changeKey(change)]: change.hidden}));
    saveChange(change);
  };

  /** One checkbox: checked while the selector offers the option. */
  const optionCheckbox: OptionCheckbox = (option, isHiddenOnServer, props = {}) => {
    const isHidden = unconfirmed[changeKey(option)] ?? isHiddenOnServer;
    return {
      isHidden,
      input: (
        <input
          type="checkbox"
          checked={!isHidden}
          aria-label={props.label}
          title={props.title}
          disabled={props.disabled}
          onChange={(e) => applyChange({...option, hidden: !e.target.checked})}
        />
      ),
    };
  };

  const productItems = products.map((item) => ({
    item,
    ...optionCheckbox(
      {channel: channel.value, kind: "product", value: item.value},
      hiddenProducts.has(item.value)
    ),
  }));
  const setItems = (RELEASES_SETS as Option[]).map((item) => ({
    item,
    ...optionCheckbox(
      {channel: SETS_CHANNEL, kind: "set", value: item.value},
      hiddenSets.has(item.value)
    ),
  }));
  const bgChange = (id: BackgroundId, set?: string): Omit<HiddenChange, "hidden"> => ({
    channel: dataChannel,
    kind: "background",
    type: dataType,
    product: product.value,
    ...(set ? {set} : {}),
    value: id,
  });
  const bgItems = bgRows.map((row) => ({
    row,
    ...optionCheckbox(bgChange(row.id), hiddenBgs.has(row.id), {label: row.label}),
  }));

  // Releases: the set columns. A background hidden in every set ("All") has
  // its set boxes cleared and disabled.
  const hiddenBySet = new Map(
    gallerySets.map((set) => [
      set.value,
      hiddenBackgroundsInSet(gallerySettings, dataType, product.value, set.value),
    ])
  );
  const bgSetRows = bgItems.map((item) => ({
    ...item,
    cells: gallerySets.map((set) => {
      const option = bgChange(item.row.id, set.value);
      const isHiddenInSet =
        unconfirmed[changeKey(option)] ?? hiddenBySet.get(set.value)?.has(item.row.id) ?? false;
      return {
        set,
        isHiddenInSet,
        isHidden: item.isHidden || isHiddenInSet,
        // Hidden in every set: the set box shows it cleared and waits for "All".
        input: optionCheckbox(option, item.isHidden || isHiddenInSet, {
          label: `${item.row.label} in ${set.label}`,
          disabled: item.isHidden,
          title: item.isHidden ? "Hidden in every set" : set.label,
        }).input,
      };
    }),
  }));
  /** Offer a background in `setValue` only: one change per mark that differs. */
  const showOnlyIn = (row: (typeof bgSetRows)[number], setValue: string) => {
    if (row.isHidden) applyChange({...bgChange(row.row.id), hidden: false});
    for (const cell of row.cells) {
      const hidden = cell.set.value !== setValue;
      if (cell.isHiddenInSet !== hidden) {
        applyChange({...bgChange(row.row.id, cell.set.value), hidden});
      }
    }
  };
  const currentSetOption = gallerySets.find((set) => set.value === currentSet);
  const hiddenHere = currentSetOption
    ? bgSetRows.filter(
        (row) => row.cells.find((cell) => cell.set.value === currentSet)?.isHidden
      ).length
    : bgItems.filter((item) => item.isHidden).length;

  const showProducts = showsOptions && products.length > 1;
  const showSets = showsOptions && dataChannel === SETS_CHANNEL;
  // The stage rules need a server that knows them, for the same reason as the
  // set and layout rules below: an older server would take a stage mark as a
  // mark for every stage.
  const productStages = channelStages(channel.value, productSettings?.stageOrder);
  const showProductStages =
    productSettings?.hiddenStageProducts !== undefined && productStages.length > 1;
  const showSetStages = setSettings?.hiddenStageSets !== undefined;
  const showBackgrounds = showsBackgrounds && bgRows.length > 0;
  // A server from before the set and layout rules sends no `hiddenLayouts`.
  // It would take a set mark as a mark for every set (it does not know the
  // field), and the database is shared with production, so those controls
  // need a server that knows them. The lightweight dev server proxies this
  // route to staging.
  const knowsSetRules = gallerySettings?.hiddenLayouts !== undefined;
  const showBgSets = showBackgrounds && gallerySets.length > 1 && knowsSetRules;
  const layoutSet =
    currentSetOption ?? (RELEASES_SETS as Option[]).find((set) => set.value === currentSet);
  const showLayouts =
    showsOptions &&
    knowsSetRules &&
    layoutVisibilityRows({
      dataChannel,
      set: layoutSet?.value,
      product: null,
      settings: gallerySettings,
    }).length > 0;
  if (!showProducts && !showSets && !showBackgrounds && !showLayouts) return null;

  return (
    <div className="default-scene-control selector-visibility-section">
      <span
        className="default-scene-caption"
        title="What the selectors offer to every user. A hidden option stays available by link and in saved scenes."
      >
        Shown in selectors
      </span>

      {showProducts && showProductStages && (
        <ProductStageVisibilityGroup
          channel={channel}
          products={products}
          stages={productStages}
          currentType={type.value}
          settings={productSettings}
          checkbox={optionCheckbox}
        />
      )}

      {showProducts && !showProductStages && (
        <CheckboxGroup
          title={`Products of ${channel.label}`}
          {...hiddenSummary(productItems)}
        >
          {productItems.map(({item, isHidden, input}) => (
            <label
              key={item.value}
              className={cn("selector-visibility-item", {"is-hidden": isHidden})}
              title={item.label}
            >
              {input}
              <span className="selector-visibility-label">{item.label}</span>
            </label>
          ))}
        </CheckboxGroup>
      )}

      {showSets && showSetStages && (
        <SetStageVisibilityGroup
          product={product}
          stages={channelStages(SETS_CHANNEL, setSettings?.stageOrder)}
          currentType={dataType}
          settings={setSettings}
          checkbox={optionCheckbox}
        />
      )}

      {showSets && !showSetStages && (
        <CheckboxGroup title="Sets" {...hiddenSummary(setItems)}>
          {setItems.map(({item, isHidden, input}) => (
            <label
              key={item.value}
              className={cn("selector-visibility-item", {"is-hidden": isHidden})}
              title={item.label}
            >
              {input}
              <span className="selector-visibility-label">{item.label}</span>
            </label>
          ))}
        </CheckboxGroup>
      )}

      {showBgSets && (
        <CheckboxGroup
          title={`Backgrounds of ${product.label} · ${type.label}`}
          summary={
            hiddenHere > 0
              ? `${hiddenHere} of ${bgRows.length} hidden in ${currentSetOption?.label ?? "this set"}`
              : `all ${bgRows.length} shown in ${currentSetOption?.label ?? "this set"}`
          }
          isChanged={hiddenHere > 0}
          className="bg-set-visibility"
        >
          <div
            className="bg-set-matrix"
            style={{gridTemplateColumns: `52px repeat(${gallerySets.length + 1}, 1fr) 22px`}}
          >
            <span />
            <span className="bg-set-head" title="Every set of the product">
              All
            </span>
            {gallerySets.map((set) => (
              <span
                key={set.value}
                className={cn("bg-set-head", {
                  "is-current": set.value === currentSet,
                  "is-hidden": hiddenSets.has(set.value),
                })}
                title={hiddenSets.has(set.value) ? `${set.label} (hidden set)` : set.label}
              >
                {set.label}
              </span>
            ))}
            <span />
            {bgSetRows.map((row) => (
              <div key={row.row.id} className="bg-set-row">
                <span
                  className={cn("bg-set-tile", {
                    "is-current": String(row.row.id) === String(bgOption),
                  })}
                  title={row.row.title}
                >
                  <span className="selector-visibility-bg-thumb" style={row.row.thumbStyle} />
                  <span className="selector-visibility-bg-number">{row.row.name}</span>
                </span>
                <span className={cn("bg-set-cell", {"is-hidden": row.isHidden})}>{row.input}</span>
                {row.cells.map((cell) => (
                  <span
                    key={cell.set.value}
                    className={cn("bg-set-cell", {
                      "is-hidden": cell.isHidden,
                      "is-current": cell.set.value === currentSet,
                    })}
                  >
                    {cell.input}
                  </span>
                ))}
                {currentSetOption ? (
                  <button
                    type="button"
                    className="bg-set-only"
                    title={`Show only in ${currentSetOption.label}`}
                    aria-label={`Show ${row.row.phrase} only in ${currentSetOption.label}`}
                    onClick={() => showOnlyIn(row, currentSetOption.value)}
                  >
                    ◎
                  </button>
                ) : (
                  <span />
                )}
              </div>
            ))}
          </div>
        </CheckboxGroup>
      )}

      {showBackgrounds && !showBgSets && (
        <CheckboxGroup
          title={`Backgrounds of ${product.label}`}
          {...hiddenSummary(bgItems)}
          className="selector-visibility-bgs"
        >
          {bgItems.map(({row, isHidden, input}) => (
            <label
              key={row.id}
              className={cn("selector-visibility-bg", {
                "is-hidden": isHidden,
                "is-current": String(row.id) === String(bgOption),
              })}
              title={row.title}
            >
              <span className="selector-visibility-bg-thumb" style={row.thumbStyle} />
              <span className="selector-visibility-bg-number">
                {input}
                {row.name}
              </span>
            </label>
          ))}
        </CheckboxGroup>
      )}

      {showLayouts && (
        <LayoutVisibilityGroup
          dataChannel={dataChannel}
          entryChannel={channel.value}
          product={product}
          set={layoutSet}
          currentType={dataType}
          settings={gallerySettings}
          checkbox={optionCheckbox}
        />
      )}

      {hasFailed && (
        <span className="default-scene-status warning">
          Couldn&apos;t save. The lists show what the server has.
        </span>
      )}
    </div>
  );
}
